// Vendor Program v2 PR 2 — borrower (buyer) portal reads for the monthly
// vendor cycle: improvement goals with their grade trail, accepted vendor
// reports, and the one-time sign-off. These payloads carry ONLY the public
// sections (§1 narrative + §2 goal grades) — private concerns / invoice
// data never reach the borrower, so the UI never renders placeholders for
// them.

import { formatPeriodMonth } from "@/design-system/lib/format";
import { portalApi } from "./client";

export type BorrowerGoalStatus = "active" | "achieved" | "retired";

export interface BorrowerGoalGradePointDto {
  periodStart: string;
  /** 0–100 progress score graded by the vendor for that month. */
  score: number;
  isComplete: boolean;
  note: string | null;
  vendorName: string;
}

export interface BorrowerGoalDto {
  id: string;
  loanId: string;
  loanLabel: string;
  title: string;
  description: string;
  completionCriteria: string | null;
  targetMonths: string | null;
  status: BorrowerGoalStatus;
  assignedVendorName: string | null;
  gradeTrail: BorrowerGoalGradePointDto[];
}

export interface BorrowerReportGoalGradeDto {
  goalId: string;
  goalTitle: string;
  score: number;
  isComplete: boolean;
  note: string | null;
}

export interface BorrowerSignOffSummaryDto {
  signedAt: string;
  serviceQuality: number;
  responsiveness: number;
  satisfaction: number;
  /** Server-computed mean of the three areas, 1 decimal. */
  overallRating: number;
  feedback: string | null;
}

export interface BorrowerVendorReportDto {
  reportId: string;
  loanId: string;
  loanLabel: string;
  vendorName: string;
  roleOnLoan: string;
  periodStart: string;
  periodEnd: string;
  acceptedAt: string | null;
  publicNarrative: string | null;
  goalGrades: BorrowerReportGoalGradeDto[];
  signedOff: boolean;
  signOff: BorrowerSignOffSummaryDto | null;
}

/** Three 1–4 star areas; overall is computed server-side. One per report —
 *  the server 400s when already signed. */
export interface SubmitVendorReportSignOffRequest {
  serviceQuality: number;
  responsiveness: number;
  satisfaction: number;
  feedback?: string;
}

// Report periods are month boundaries serialized in UTC — format in UTC so
// a local timezone never shifts "2026-06-01" back into May.
//
// This was the ONLY tier that got it right: the admin surfaces rendered the
// same periods through the tz-aware formatter and read June as May. Both
// tiers now derive the label from one helper in design-system/lib/format
// (on the portal import allow-list), so they cannot drift apart again.
function monthYear(iso: string, month: "short" | "long"): string {
  return formatPeriodMonth(iso, month);
}

/** "June 2026" — headings ("{vendorName}'s June 2026 report"). */
export function formatReportMonth(iso: string): string {
  return monthYear(iso, "long");
}

/** "Jun 2026" — compact grade-trail / history rows. */
export function formatReportMonthShort(iso: string): string {
  return monthYear(iso, "short");
}

export const portalBorrowerApi = {
  listGoals: () => portalApi.get<BorrowerGoalDto[]>("/buyer/goals"),
  /** Pending sign-off (signedOff=false) first — server-ordered. */
  listVendorReports: () =>
    portalApi.get<BorrowerVendorReportDto[]>("/buyer/vendor-reports"),
  getVendorReport: (reportId: string) =>
    portalApi.get<BorrowerVendorReportDto>(`/buyer/vendor-reports/${reportId}`),
  signOffVendorReport: (reportId: string, body: SubmitVendorReportSignOffRequest) =>
    portalApi.post<BorrowerVendorReportDto>(
      `/buyer/vendor-reports/${reportId}/sign-off`,
      body,
    ),
};
